import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import AppHeader from '../components/AppHeader';
import MarkdownRenderer from '../components/docs/MarkdownRenderer';

interface ExportDoc {
  slug: string;
  title: string;
  content: string;
}

// Doc content typically starts with a "# Title" heading matching doc.title.
// This section already renders its own styled <h1>{doc.title}</h1> above the
// content, so the markdown's leading H1 is stripped to avoid showing the
// title twice.
function stripLeadingH1(content: string): string {
  return content.replace(/^\s*#[ \t]+.+\r?\n?/, '');
}

export default function CollectionPdfExport() {
  const { org, collection } = useParams<{ org: string; collection: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [collectionName, setCollectionName] = useState('');
  const [docs, setDocs] = useState<ExportDoc[]>([]);
  const [readyCount, setReadyCount] = useState(0);
  const [allReady, setAllReady] = useState(false);
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!org || !collection) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    async function load() {
      setError(null);
      setRequiresAuth(false);
      try {
        const listFetch = session?.user
          ? api.docs.list(org!, collection!)
          : api.docs.listPublic(org!, collection!);
        const listData = await listFetch;

        if (listData.single_doc && listData.document) {
          navigate(`/${org}/${collection}/${listData.document.slug}`, { replace: true });
          return;
        }

        const items = (listData.documents ?? [])
          .slice()
          .sort((a, b) => a.display_order - b.display_order);

        const fullDocs = await Promise.all(
          items.map((item) =>
            session?.user
              ? api.docs.get(org!, collection!, item.slug)
              : api.docs.getPublic(org!, collection!, item.slug),
          ),
        );

        setDocs(
          items.map((item, i) => ({
            slug: item.slug,
            title: item.title,
            content: stripLeadingH1(fullDocs[i].document.content),
          })),
        );
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          setRequiresAuth(true);
        } else {
          setError(err?.message ?? 'Failed to load documentation');
        }
      } finally {
        setLoading(false);
      }
    }

    api.orgs.collection(org!, collection!)
      .then((c) => setCollectionName(c.name))
      .catch(() => {});

    load();
  }, [org, collection, session, authLoading, navigate]);

  // Print/export becomes available once every document has finished
  // rendering (incl. async mermaid diagrams).
  useEffect(() => {
    if (docs.length === 0 || readyCount < docs.length) return;
    setAllReady(true);
  }, [readyCount, docs]);

  const handleReady = () => setReadyCount((c) => c + 1);
  const handlePrint = () => window.print();

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading documentation...</p>
        </div>
      </div>
    );
  }

  if (requiresAuth) {
    const currentPath = window.location.pathname;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <h1 className="text-2xl font-bold text-foreground mb-2">Authentication Required</h1>
          <p className="text-muted-foreground mb-6">
            This documentation is private. Please sign in to export it.
          </p>
          <Link
            to={`/auth/login?redirect=${encodeURIComponent(currentPath)}`}
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition"
          >
            Sign in to continue
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => navigate(`/${org}/${collection}`)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Back to collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <AppHeader showDashboardLink />

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        <div className="flex items-center justify-between mb-8 no-print">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{collectionName || collection} — PDF Export</h1>
            <p className="text-sm text-muted-foreground">{org} / {collection}</p>
          </div>
          <button
            onClick={handlePrint}
            disabled={!allReady}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Printer className="w-4 h-4" />
            {allReady ? 'Print / Save as PDF' : 'Preparing documents…'}
          </button>
        </div>

        {docs.length === 0 ? (
          <p className="text-muted-foreground">This collection doesn't have any documents yet.</p>
        ) : (
          <>
            <section className="mb-12 bg-background rounded-xl shadow-sm px-8 py-8">
              <h2 className="text-xl font-semibold mb-4 text-foreground">Table of Contents</h2>
              <ol className="space-y-2">
                {docs.map((doc) => (
                  <li key={doc.slug}>
                    <a href={`#pdf-doc-${doc.slug}`} className="text-primary hover:underline font-medium">
                      {doc.title}
                    </a>
                  </li>
                ))}
              </ol>
            </section>

            {docs.map((doc) => (
              <section
                key={doc.slug}
                id={`pdf-doc-${doc.slug}`}
                className="pdf-export-section bg-background rounded-xl shadow-sm px-8 py-8 mb-8"
              >
                <h1 className="text-3xl font-bold mb-6 text-foreground border-b border-border pb-4">{doc.title}</h1>
                <MarkdownRenderer content={doc.content} onReady={handleReady} />
              </section>
            ))}
          </>
        )}
      </main>
    </div>
  );
}
